import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import AdBanner from '../../components/ADS/AdBanner';
import { getCategoryIcon } from '../../utils/constants';

const StatCard = ({ value, label, color }) => (
  <div className="card text-center flex-1 p-5 rounded-2xl min-w-[110px]">
    <div className="font-black text-3xl mb-1 font-sora" style={{ color }}>{value}</div>
    <div className="text-xs font-bold uppercase tracking-wider text-dim">{label}</div>
  </div>
);

const FeatureCard = ({ icon, title, desc, accent }) => (
  <div className="card flex gap-3.5 items-start p-5 rounded-2xl">
    <div className="w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0" style={{ background: accent }}>
      {icon}
    </div>
    <div>
      <div className="font-extrabold text-sm mb-1 text-main font-sora">{title}</div>
      <div className="text-sm text-sub leading-relaxed">{desc}</div>
    </div>
  </div>
);

const STATS = [
  { value: '1000+', label: 'Free Games',   color: '#4f8ef7' },
  { value: '50+',   label: 'Categories',   color: '#a855f7' },
  { value: '10M+',  label: 'Players',      color: '#22c55e' },
  { value: '100%',  label: 'Free Forever', color: '#f59e0b' },
];

const FEATURES = [
  { icon: '⚡', title: 'Instant Play',    desc: 'No downloads, no installs. Click and play any game in seconds directly in your browser.',        accent: 'rgba(79,142,247,0.15)'  },
  { icon: '📱', title: 'All Devices',     desc: 'Fully optimized for desktop, tablet, and mobile. Same great experience on every screen.',         accent: 'rgba(168,85,247,0.15)'  },
  { icon: '🆓', title: 'Always Free',     desc: 'Every single game onCar Games - Play Free Driving and Racing Games Online |Car Games - Free Driving and Racing Games Online is 100% free. No subscriptions, no hidden fees, ever.',          accent: 'rgba(34,197,94,0.15)'   },
  { icon: '🔒', title: 'Safe & Secure',   desc: 'Kid-friendly platform with no sign-up required. Your privacy is always protected.',                accent: 'rgba(245,158,11,0.15)'  },
  { icon: '🎮', title: 'Huge Collection', desc: 'From action and puzzle to racing and sports — thousands of games across every genre.',             accent: 'rgba(239,68,68,0.15)'   },
  { icon: '🔄', title: 'Daily Updates',   desc: "New games added regularly so there's always something fresh and exciting to discover.",            accent: 'rgba(6,182,212,0.15)'   },
];

const CATEGORIES = ['Action','Adventure','Puzzle','Racing','Sports','Arcade','Multiplayer','Casual','Shooting','Strategy','Hypercasual','Stickman'];

const POPULAR_GAMES = [
  { slug: 'ludo-dash',            name: 'Ludo Dash',            emoji: '🎲' },
  { slug: 'fruit-chop',           name: 'Fruit Chop',           emoji: '🍉' },
  { slug: 'escape-run',           name: 'Escape Run',           emoji: '🏃' },
  { slug: 'save-your-pinky',      name: 'Save Your Pinky',      emoji: '🩷' },
  { slug: 'happy-kittens-puzzle', name: 'Happy Kittens Puzzle', emoji: '🐱' },
  { slug: 'stickman-hook',        name: 'Stickman Hook',        emoji: '🕹️' },
];

const BENEFITS = [
  { icon: '🧠', text: 'Sharpens critical thinking & strategy' },
  { icon: '😌', text: 'Reduces stress and relaxes your mind' },
  { icon: '⚡', text: 'Boosts reflexes and reaction time' },
  { icon: '🎨', text: 'Sparks creativity and imagination' },
  { icon: '🤝', text: 'Builds social skills via multiplayer' },
  { icon: '😊', text: 'Releases dopamine — keeps you happy' },
];

function About() {
  return (
    <div className="px-4 py-5 pb-12">
      <Helmet>
        <title>{'AboutCar Games - Play Free Driving and Racing Games Online |Car Games - Free Driving and Racing Games Online |AboutCar Games Guides, News and Driving Tips | Car Games | Free Driving and Racing Games'}</title>
        <meta name="description" content="Learn aboutCar Games - Play Free Driving and Racing Games Online |Car Games - Free Driving and Racing Games Online — your ultimate destination for free online browser games. No downloads, no logins. Play action, puzzle, racing games and more!" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://cargames.site/about" />
      </Helmet>

      <div className="max-w-4xl mx-auto">

        {/* Hero */}
        <div className="rounded-3xl overflow-hidden mb-8 relative text-center border-theme bg-gradient-to-br from-[#1a1040] via-[#0f1117] to-[#0a1628] p-[clamp(28px,5vw,48px)_clamp(16px,4vw,36px)]">
          <div className="absolute -top-10 -left-10 w-48 h-48 rounded-full pointer-events-none bg-[rgba(79,142,247,0.12)] blur-[40px]" />
          <div className="absolute -bottom-10 -right-10 w-48 h-48 rounded-full pointer-events-none bg-[rgba(168,85,247,0.12)] blur-[40px]" />
          <div className="relative z-10">
            <div className="text-5xl mb-3">🎮</div>
            <h1 className="font-black mb-3 leading-tight font-sora text-[clamp(22px,5vw,32px)] bg-gradient-to-br from-[#4f8ef7] to-[#a855f7] bg-clip-text text-transparent">
              Welcome toCar Games - Play Free Driving and Racing Games Online |Car Games - Free Driving and Racing Games Online
            </h1>
            <p className="text-sm leading-relaxed mx-auto mb-6 text-[#9aa0b8] max-w-[560px]">
              Your ultimate free online gaming hub. No downloads, no sign-ups — just pure gaming fun on any device, anytime.
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Link to="/category/all-games" className="btn-accent px-7 py-3 text-sm">🕹️ Play Now</Link>
              <Link to="/blog" className="px-7 py-3 rounded-full font-bold text-sm no-underline bg-white/[0.08] text-[#f0f2f8] border border-white/[0.12]">📝 Read Blog</Link>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {STATS.map((s, i) => <StatCard key={i} {...s} />)}
        </div>

        {/* What is */}
        <div className="section-card mb-7">
          <h2 className="font-extrabold text-lg flex items-center gap-2.5 mb-3.5 text-main font-sora">
            <span className="rounded-lg px-2 py-1 text-base bg-gradient-to-br from-[#4f8ef7] to-[#a855f7]">🌐</span>
            What isCar Games - Play Free Driving and Racing Games Online |Car Games - Free Driving and Racing Games Online?
          </h2>
          <p className="text-sm text-sub leading-loose mb-3">
            <strong className="text-main">Car Games</strong> is a free online gaming platform where you can instantly play hundreds of browser-based games — no downloads, no registrations, no hassle.
          </p>
          <p className="text-sm text-sub leading-loose">
            Our platform is built for speed and simplicity. Every game loads instantly in your browser and works perfectly on desktop, tablet, and mobile.
          </p>
        </div>

        {/* Features */}
        <h2 className="font-extrabold text-lg flex items-center gap-2 mb-4 text-main font-sora">
          ✅ Why ChooseCar Games - Play Free Driving and Racing Games Online |Car Games - Free Driving and Racing Games Online?
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
          {FEATURES.map((f, i) => <FeatureCard key={i} {...f} />)}
        </div>

        <AdBanner className="mb-8" />

        {/* Benefits */}
        <div className="section-card mb-7">
          <h2 className="font-extrabold text-lg flex items-center gap-2 mb-4 text-main font-sora">
            ⚡ Benefits of Playing Online Games
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {BENEFITS.map((b, i) => (
              <div key={i} className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl border-theme bg-hover-theme">
                <span className="text-xl flex-shrink-0">{b.icon}</span>
                <span className="text-sm font-semibold text-sub">{b.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Popular Games */}
        <div className="section-card mb-7">
          <h2 className="font-extrabold text-lg flex items-center gap-2 mb-4 text-main font-sora">
            🏆 Popular Games
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {POPULAR_GAMES.map(({ slug, name, emoji }) => (
              <Link key={slug} to={`/game/${slug}`}
                className="flex items-center gap-3 px-3.5 py-3 rounded-xl no-underline border-theme bg-hover-theme transition-colors hover:border-game-blue">
                <span className="text-2xl flex-shrink-0">{emoji}</span>
                <span className="text-sm font-bold truncate text-main">{name}</span>
              </Link>
            ))}
          </div>
          <div className="mt-3.5 text-center">
            <Link to="/category/all-games" className="btn-outline">View All Games →</Link>
          </div>
        </div>

        {/* Categories */}
        <div className="section-card mb-7">
          <h2 className="font-extrabold text-lg flex items-center gap-2 mb-4 text-main font-sora">
            🌐 Explore Categories
          </h2>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map(cat => (
              <Link key={cat} to={`/category/${cat.toLowerCase().replace(/\s+/g, '-')}`}
                className="flex items-center gap-1.5 text-sm font-bold px-3.5 py-1.5 rounded-xl no-underline border-theme bg-hover-theme text-sub transition-all hover:text-main hover:border-game-blue">
                <span>{getCategoryIcon(cat)}</span>{cat}
              </Link>
            ))}
          </div>
        </div>

        {/* Mission */}
        <div className="rounded-2xl text-center mb-7 p-7 border border-blue-500/20 bg-gradient-to-br from-[rgba(79,142,247,0.08)] to-[rgba(168,85,247,0.08)]">
          <div className="text-4xl mb-3">🚀</div>
          <h2 className="font-extrabold text-lg mb-2.5 text-main font-sora">Our Mission</h2>
          <p className="text-sm text-sub leading-loose mx-auto max-w-[580px]">
            We believe gaming should be accessible to everyone — free, instant, and fun. Our mission is to build the world's best free browser gaming platform where anyone can jump in and play without barriers.
          </p>
        </div>

        {/* Contact CTA */}
        <div className="section-card flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-extrabold text-base mb-1 text-main font-sora">💬 Have Questions or Suggestions?</div>
            <div className="text-sm text-dim">We'd love to hear from you. Reach out anytime!</div>
          </div>
          <Link to="/contact" className="btn-accent px-6 py-2.5 text-sm flex-shrink-0">Contact Us →</Link>
        </div>

      </div>
    </div>
  );
}

export default About;
